// import { useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
// import { Input } from '@/components/ui/input'
// import { Label } from '@/components/ui/label'
// import { useToast } from '@/components/ui/use-toast'
import { Separator } from '@/components/ui/separator'
import { Trash2, Loader2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from '@/components/ui/select'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'

interface Classroom {
  id: string
  name: string
  capacity?: number
  type?: string
}

interface ClassroomsConfigurationProps {
  classrooms: Classroom[]
  isLoading: boolean
  onDeleteClassroom: (classroomId: string) => void
  refetchClassrooms?: () => Promise<any>
}

export default function ClassroomsConfiguration({
  classrooms,
  isLoading,
  onDeleteClassroom,
  refetchClassrooms,
}: ClassroomsConfigurationProps) {
  const { t } = useTranslation('timetable', { keyPrefix: 'timetable-folder.classroomsConfiguration' })

  // Check for invalid data and trigger refetch if needed
  useEffect(() => {
    // Only check after loading is complete
    if (!isLoading && refetchClassrooms) {
      // Check if we have classrooms but they're invalid
      const hasClassrooms = classrooms && classrooms.length > 0
      const invalidClassrooms = hasClassrooms 
        ? classrooms.filter(c => !c || !c.id || !c.name || c.name.trim() === '')
        : []
      
      // If we have invalid classrooms (all classrooms are invalid or some are invalid)
      if (invalidClassrooms.length > 0) {
        console.warn('⚠️ ClassroomsConfiguration: Invalid data detected, refetching...', {
          invalidCount: invalidClassrooms.length,
          totalCount: classrooms?.length || 0
        })
        
        // Use refetch function with a small delay to avoid race conditions
        const timeoutId = setTimeout(() => {
          refetchClassrooms().catch((error) => {
            console.error('Failed to refetch classrooms:', error)
            // Fallback to reload if refetch fails after retry
            setTimeout(() => {
              if (refetchClassrooms) {
                refetchClassrooms().catch(() => {
                  window.location.reload()
                })
              } else {
                window.location.reload()
              }
            }, 2000)
          })
        }, 500)
        
        return () => clearTimeout(timeoutId)
      }
    }
  }, [classrooms, isLoading, refetchClassrooms])

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Separator className="my-4" />

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : classrooms.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('empty')}
            </div>
          ) : (
            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('table.name')}</TableHead>
                    <TableHead>{t('table.capacity')}</TableHead>
                    <TableHead>{t('table.type')}</TableHead>
                    <TableHead className="w-[100px]">{t('table.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {classrooms
                    .filter((classroom) => classroom && classroom.id && classroom.name && classroom.name.trim() !== '')
                    .map((classroom) => (
                      <TableRow key={classroom.id}>
                        <TableCell className="font-medium">
                          {classroom.name}
                        </TableCell>
                        <TableCell>
                          {classroom.capacity || t('notSpecified')}
                        </TableCell>
                        <TableCell>
                          {classroom.type || t('notSpecified')}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDeleteClassroom(classroom.id)}
                            title={t('deleteTitle')}
                            className="min-h-[44px] min-w-[44px]"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
